// @ts-strict-ignore
import type { SerializedStyles } from "@emotion/serialize";
import { useTranslation } from "react-i18next";

import { Accordion } from "@aviary";
import { EmersonAllCategories } from "@shared/components/CatalogSearch/Filters/EmersonCategory/EmersonAllCategories/EmersonAllCategories";
import { FilterType } from "@shared/hooks/useAnalyticsLogger/useAnalyticsLogger";
import { useCatalogSearch } from "@shared/hooks/useCatalogSearch/useCatalogSearch";
import { l } from "@shared/locales/i18n";

import type { CategoryFilter } from "../../CatalogSearch";
import { FilterTitle } from "../shared/FilterTitle/FilterTitle";

import * as styles from "./EmersonCategory.styles";

interface Props {
  isBold?: boolean;
  logFilterEvent: (entityName: string, entityType: string) => void;
  isDefaultExpanded?: boolean;
  customAccordionStyle?: SerializedStyles[] | SerializedStyles;
}

const EmersonCategory = ({
  isBold,
  logFilterEvent,
  isDefaultExpanded,
  customAccordionStyle,
}: Props) => {
  const { t } = useTranslation();
  const { activeTaxonomies, setActiveTaxonomies } = useCatalogSearch();

  const isCategoryActive = (id: string): boolean =>
    activeTaxonomies.some(activeCategory => activeCategory.id === id);
  const taxonomyIds = activeTaxonomies.map(({ id }) => id);

  const onCategorySelect = (categorySelected: CategoryFilter) => {
    const distanceFromTop = window?.scrollY;
    if (isCategoryActive(categorySelected.id)) {
      setActiveTaxonomies(activeTaxonomies.filter(category => category.id !== categorySelected.id));
    } else {
      logFilterEvent(categorySelected.name, FilterType.CATEGORY);
      setActiveTaxonomies([...activeTaxonomies, categorySelected]);
    }
    requestAnimationFrame?.(() => window?.scrollTo(0, distanceFromTop));
  };

  return (
    <Accordion
      css={[styles.accordion, customAccordionStyle]}
      isDefaultExpanded={isDefaultExpanded || activeTaxonomies?.length > 0}
      coreTriggerContent={
        <FilterTitle
          title={t(l.sharedCatalog.filter.Category)}
          count={activeTaxonomies?.length}
          isBadge
          isSecondaryWeight={isBold}
        />
      }
    >
      <EmersonAllCategories
        taxonomyIds={taxonomyIds}
        isCategoryActive={isCategoryActive}
        onCategorySelect={onCategorySelect}
      />
    </Accordion>
  );
};

export { EmersonCategory };
